import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Checkbox, Spin, List, Divider, Input, Space, Tag, Typography, Tooltip } from 'antd';
import { SearchOutlined, CloseCircleOutlined, InfoCircleOutlined, StarOutlined } from '@ant-design/icons';

const { Text } = Typography;

/**
 * Metrikleri seçmek için modal bileşeni
 */
const MetricSelector = ({ 
  open, 
  onCancel, 
  onSave, 
  allMetricsData, 
  monthlyMetricsData = {},
  brands, 
  selectedMetrics = [],
  defaultMetricDescriptions = []
}) => {
  const [loading, setLoading] = useState(false);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [checkedMetrics, setCheckedMetrics] = useState([]);
  const [metricDescriptions, setMetricDescriptions] = useState([]); // Seçili metriklerin açıklamaları
  const [searchText, setSearchText] = useState('');
  const [filteredMetrics, setFilteredMetrics] = useState([]);
  const [metricIdsByDescription, setMetricIdsByDescription] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Memoize brands length ve open kombinasyonu
  const brandsCount = useMemo(() => brands?.length || 0, [brands]);
  
  // Component mount olduğunda veya open değiştiğinde metrik verilerini hazırla
  useEffect(() => {
    if (!open) {
      setDataLoaded(false);
      return;
    }
    
    // Eğer data zaten yüklenmişse ve brands değişmemişse, yeniden yükleme
    if (dataLoaded && availableMetrics.length > 0) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Metrik bilgilerini saklayacak nesneler
      const uniqueDescriptions = new Set(); // Benzersiz açıklamaları saklamak için
      const metricNameMap = {}; // ID -> {name, description, dataType}
      const descriptionToIds = {}; // Description -> ID listesi
      
      // Metrik verilerini topla
      brands.forEach(brand => {
        
        let brandMetrics = [];
        
        // Önce allMetricsData'dan al
        if (allMetricsData && allMetricsData[brand.id] && allMetricsData[brand.id].filteredMetrics) {
          brandMetrics = allMetricsData[brand.id].filteredMetrics;
        }
        // Eğer yoksa monthlyMetricsData'dan al
        else if (monthlyMetricsData && monthlyMetricsData[brand.id] && monthlyMetricsData[brand.id].metrics) {
          brandMetrics = monthlyMetricsData[brand.id].metrics;
        }
        
        if (brandMetrics && brandMetrics.length > 0) {
          // Her bir metriği ekle
          brandMetrics.forEach(metric => {
            // Metrik açıklamasını standartlaştır (boşsa name kullan)
            const description = (metric.description || metric.name || `Metrik ${metric.id}`).trim();
            
            // Benzersiz açıklamaları ekle
            uniqueDescriptions.add(description);
            
            // Metrik bilgilerini sakla
            metricNameMap[metric.id] = {
              name: metric.name,
              description: description,
              dataType: metric.dataType || 'number',
              internalNotes: metric.internalNotes,
              isAutoCalculated: metric.isAutoCalculated
            };
            
            // Açıklamaya göre metrik ID'lerini grupla
            if (!descriptionToIds[description]) {
              descriptionToIds[description] = [];
            }
            if (!descriptionToIds[description].includes(metric.id)) {
              descriptionToIds[description].push(metric.id);
            }
          });
        }
      });
      
      
      // Açıklamalara göre sıralanmış metrik dizisi oluştur
      const sortedDescriptions = Array.from(uniqueDescriptions).sort();
      const metricsArray = sortedDescriptions.map(description => {
        const representativeId = descriptionToIds[description][0];
        const representativeMetric = metricNameMap[representativeId];
        
        return {
          description,
          // Bu açıklamaya sahip ilk metriğin ID'sini temsili olarak kullan
          representativeId: representativeId,
          // Bu açıklamaya sahip tüm metrik ID'leri
          allIds: descriptionToIds[description],
          // Representative metrik bilgileri
          internalNotes: representativeMetric?.internalNotes,
          isAutoCalculated: representativeMetric?.isAutoCalculated,
          name: representativeMetric?.name
        };
      });
      
      setAvailableMetrics(metricsArray);
      setFilteredMetrics(metricsArray);
      setMetricIdsByDescription(descriptionToIds);
      
      // Önceden seçilmiş metrikleri checkbox'a yansıt
      // Sadece checkedMetrics boşsa yükle, böylece tarih değişikliğinde kaybolmaz
      if (selectedMetrics && selectedMetrics.length > 0 && checkedMetrics.length === 0) {
        // Seçili metrikleri description gruplarına dönüştür
        const selectedDescriptions = new Set();
        const selectedDescriptionArray = [];
        
        selectedMetrics.forEach(id => {
          if (metricNameMap[id]) {
            const desc = metricNameMap[id].description;
            if (!selectedDescriptions.has(desc)) {
              selectedDescriptions.add(desc);
              selectedDescriptionArray.push(desc);
            }
          }
        });
        
        // Seçili metrik ID'leri yerine description gruplarını kullan
        setCheckedMetrics(Array.from(selectedDescriptions));
        setMetricDescriptions(selectedDescriptionArray);
      }
    } catch (error) {
      console.error('Metrikler yüklenirken hata oluştu:', error);
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  }, [open, brandsCount, dataLoaded, availableMetrics.length]); // Optimize edilmiş dependencies
  
  // Arama kısmı değiştiğinde filtreleme yap
  useEffect(() => {
    if (!searchText) {
      setFilteredMetrics(availableMetrics);
      return;
    }
    
    const filtered = availableMetrics.filter(metric => 
      metric.description.toLowerCase().includes(searchText.toLowerCase())
    );
    
    setFilteredMetrics(filtered);
  }, [searchText, availableMetrics]);
  
  // Checkbox değişikliğini işle
  const handleCheckboxChange = useCallback((description) => {
    const newChecked = [...checkedMetrics];
    const metricIndex = newChecked.indexOf(description);
    
    if (metricIndex === -1) {
      newChecked.push(description);
      
      // Açıklamayı da ekle
      const newDescriptions = [...metricDescriptions];
      newDescriptions.push(description);
      setMetricDescriptions(newDescriptions);
    } else {
      newChecked.splice(metricIndex, 1);
      
      // Açıklamayı da kaldır
      const newDescriptions = [...metricDescriptions];
      newDescriptions.splice(metricIndex, 1);
      setMetricDescriptions(newDescriptions);
    }
    
    setCheckedMetrics(newChecked);
  }, [checkedMetrics, metricDescriptions]);
  
  // Seçili etiket silme
  const handleTagClose = useCallback((index) => {
    const newChecked = [...checkedMetrics];
    const newDescriptions = [...metricDescriptions];
    
    newChecked.splice(index, 1);
    newDescriptions.splice(index, 1);
    
    setCheckedMetrics(newChecked);
    setMetricDescriptions(newDescriptions);
  }, [checkedMetrics, metricDescriptions]);
  
  // Kaydet butonu - description'dan ID'lere dönüştür
  const handleSave = useCallback(() => {
    // Seçili açıklamalardan tüm metrik ID'lerini al
    const allSelectedIds = [];
    
    checkedMetrics.forEach(description => {
      const ids = metricIdsByDescription[description] || [];
      allSelectedIds.push(...ids);
    });
    
    onSave(allSelectedIds, metricDescriptions);
  }, [checkedMetrics, metricIdsByDescription, metricDescriptions, onSave]);
  
  // İptal butonu
  const handleCancel = useCallback(() => {
    // İptal edildiğinde metrik seçimleri sıfırlanmaz, eski haline döner
    onCancel();
  }, [onCancel]);

  // Default metrikleri seç
  const handleSelectDefaults = useCallback(() => {
    if (defaultMetricDescriptions.length === 0) return;

    // Default metriklerden mevcut olanları bul
    const availableDefaults = defaultMetricDescriptions.filter(desc =>
      availableMetrics.some(metric => metric.description === desc)
    );

    // Mevcut seçimlere default metrikleri ekle (tekrar eklenmesini önle)
    const newCheckedMetrics = [...checkedMetrics];
    const newMetricDescriptions = [...metricDescriptions];

    availableDefaults.forEach(desc => {
      if (!newCheckedMetrics.includes(desc)) {
        newCheckedMetrics.push(desc);
        newMetricDescriptions.push(desc);
      }
    });

    setCheckedMetrics(newCheckedMetrics);
    setMetricDescriptions(newMetricDescriptions);
  }, [defaultMetricDescriptions, availableMetrics, checkedMetrics, metricDescriptions]);
  
  
  return (
    <Modal
      title="Metrik Seçimi"
      open={open}
      onCancel={handleCancel}
      width={700}
      zIndex={1050}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          İptal
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          Kaydet
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input
            placeholder="Metrik ara..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ flex: 1 }}
          />
          {defaultMetricDescriptions.length > 0 && (
            <Button 
              icon={<StarOutlined />}
              onClick={handleSelectDefaults}
              type="default"
            >
              Default Metrikleri Seç
            </Button>
          )}
        </div>
        
        {defaultMetricDescriptions.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              Default Metrikler: {defaultMetricDescriptions.join(', ')}
            </Text>
          </div>
        )}
      </div>
      
      {checkedMetrics.length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Text strong>Seçili Metrikler:</Text>
            <div style={{ marginTop: 8 }}>
              <Space size={[0, 8]} wrap>
                {checkedMetrics.map((description, index) => {
                  const isDefault = defaultMetricDescriptions.includes(description);
                  return (
                    <Tag 
                      key={description} 
                      closable
                      onClose={() => handleTagClose(index)}
                      style={{ marginBottom: 8 }}
                      color={isDefault ? 'gold' : 'default'}
                      icon={isDefault ? <StarOutlined /> : null}
                    >
                      {description}
                    </Tag>
                  );
                })}
              </Space>
            </div>
          </div>
          <Divider style={{ margin: '12px 0' }} />
        </>
      )}
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin />
          <p>Metrikler yükleniyor...</p>
        </div>
      ) : (
        <List
          size="small"
          bordered
          dataSource={filteredMetrics}
          style={{ height: '400px', overflowY: 'auto' }}
          renderItem={metric => {
            const isDefault = defaultMetricDescriptions.includes(metric.description);
            return (
              <List.Item>
                <Checkbox
                  checked={checkedMetrics.includes(metric.description)}
                  onChange={() => handleCheckboxChange(metric.description)}
                >
                  <Space align="center">
                    <span style={{ fontWeight: isDefault ? 'bold' : 'normal' }}>
                      {isDefault && <StarOutlined style={{ color: '#faad14', marginRight: 4 }} />}
                      {metric.description}
                    </span>
                    <Tooltip title={
                      isDefault ? 
                        "Default Metrik - " + (metric.internalNotes || (metric.isAutoCalculated ? "Otomatik Hesaplanan" : "Manuel Metrik")) :
                        (metric.internalNotes || (metric.isAutoCalculated ? "Otomatik Hesaplanan" : "Manuel Metrik"))
                    }>
                      <InfoCircleOutlined style={{ color: '#faad14' }} />
                    </Tooltip>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ({metric.allIds.length} metrik)
                    </Text>
                  </Space>
              </Checkbox>
            </List.Item>
            );
          }}
          locale={{ emptyText: 'Metrik bulunamadı' }}
        />
      )}
    </Modal>
  );
};

export default MetricSelector; 